import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GraduationCap, ArrowRight, Eye, EyeOff } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { countryCodes } from '@/lib/countries';
import { useLang } from '@/contexts/LangContext';

export default function RegisterPage() {
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', password: '', phone: '', country_code: '+971' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const { t } = useLang();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.password || !form.phone) {
      toast.error(t('alerts.required_fields'));
      return;
    }
    if (form.password.length < 6) {
      toast.error(t('alerts.password_min'));
      return;
    }
    setLoading(true);
    try {
      const result = await register(form);
      if (result?.verification_required) {
        toast.success(t('verify.check_title'));
        const redirectParam = redirectTo ? `&redirect=${encodeURIComponent(redirectTo)}` : '';
        navigate(`/verify-email/sent?email=${encodeURIComponent(form.email)}${redirectParam}`);
        return;
      }
      toast.success(t('dashboard.welcome') + ' ' + result.first_name + '!');
      if (redirectTo) {
        navigate(redirectTo);
      } else {
        navigate('/dashboard/student');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.detail || t('alerts.registration_failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="register-page" className="min-h-[80vh] flex">
      {/* Left Brand Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary relative overflow-hidden items-center justify-center p-12">
        <div className="relative z-10 max-w-md text-primary-foreground">
          <div className="flex items-center gap-2.5 mb-8">
            <div className="h-10 w-10 rounded-xl bg-primary-foreground/20 flex items-center justify-center">
              <GraduationCap className="h-6 w-6" />
            </div>
            <span className="text-2xl font-bold">{t('brand.name')}</span>
          </div>
          <h2 className="text-3xl font-semibold mb-4 leading-tight">{t('auth.begin_journey')}</h2>
          <p className="text-sm opacity-80 leading-relaxed">{t('auth.begin_desc')}</p>
          <div className="mt-8 space-y-3">
            {['auth.personalized', 'auth.progress', 'auth.course_access', 'auth.community'].map((key) => (
              <div key={key} className="flex items-center gap-2 text-sm opacity-80">
                <div className="h-1.5 w-1.5 rounded-full bg-primary-foreground" />
                <span>{t(key)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <div className="mb-8">
            <h1 className="text-2xl font-semibold mb-2">{t('auth.create_account')}</h1>
            <p className="text-sm text-muted-foreground">{t('auth.create_desc')}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm font-medium mb-1.5">{t('auth.first_name')} *</Label>
                <Input className="rounded-lg" placeholder={t('auth.first_name')} value={form.first_name} onChange={e => setForm(f => ({ ...f, first_name: e.target.value }))} />
              </div>
              <div>
                <Label className="text-sm font-medium mb-1.5">{t('auth.last_name')} *</Label>
                <Input className="rounded-lg" placeholder={t('auth.last_name')} value={form.last_name} onChange={e => setForm(f => ({ ...f, last_name: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5">{t('auth.email')} *</Label>
              <Input type="email" className="rounded-lg" placeholder={t('auth.email')} value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} data-testid="auth-email-input" />
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-sm font-medium mb-1.5">{t('auth.code')}</Label>
                <Select value={form.country_code} onValueChange={v => setForm(f => ({ ...f, country_code: v }))} data-testid="auth-country-code-select">
                  <SelectTrigger className="rounded-lg"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {countryCodes.map(c => <SelectItem key={c.code} value={c.code}>{c.code} {c.country}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label className="text-sm font-medium mb-1.5">{t('auth.phone')} *</Label>
                <Input type="tel" className="rounded-lg" placeholder={t('auth.phone')} value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} data-testid="auth-phone-input" />
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium mb-1.5">{t('auth.password')} *</Label>
              <div className="relative">
                <Input type={showPw ? 'text' : 'password'} className="rounded-lg pr-10" placeholder={t('auth.min_chars')} value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} data-testid="auth-password-input" />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" onClick={() => setShowPw(!showPw)}>
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" className="w-full rounded-xl h-11" disabled={loading} data-testid="auth-register-submit-button">
              {loading ? t('auth.creating') : t('auth.create_account')} <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </form>

          <Separator className="my-6" />

          <p className="text-center text-sm text-muted-foreground">
            {t('auth.has_account')}{' '}
            <Link to={redirectTo ? `/login?redirect=${encodeURIComponent(redirectTo)}` : '/login'} className="text-primary font-medium hover:underline" data-testid="goto-login-link">{t('auth.sign_in_link')}</Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
}
